import {useEffect, useState} from 'react';

/**
 * Checks if the element with the specified ID is currently focused.
 *
 * @param {string} elementId - The ID of the DOM element to check for focus.
 * @returns {boolean} Returns true if the specified element is focused, otherwise false.
 */
const checkElementFocus = (elementId: string): boolean => {
    const element = document.getElementById(elementId);
    if (!element) return false;
    return document.activeElement === element;
};

/**
 * A custom hook that determines whether a specific DOM element, identified by its `elementId`, is currently focused.
 *
 * @param {string} elementId - The ID of the DOM element to monitor for focus changes.
 * @returns {boolean} A boolean value indicating whether the specified element is currently focused.
 */
const useFocus = (elementId: string) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);

    useEffect(() => {
        const handleFocusChange = () => {
            setIsFocused(checkElementFocus(elementId))
        };
        document.addEventListener('focusin', handleFocusChange)
        document.addEventListener('focusout', handleFocusChange)
        setIsFocused(checkElementFocus(elementId))
        return () => {
            document.removeEventListener('focusin', handleFocusChange)
            document.removeEventListener('focusout', handleFocusChange)
        };
    }, [elementId])

    return isFocused;
};
export default useFocus;